import React, { useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TextField,
} from "@mui/material";
import axios from "axios";
import { baseUrl, config, requestID } from "../../../common/utils";

const auditedPurchases = [
  {
    id: 1,
    item: "Printer",
    quantity: 3,
    price: 45000,
    department: "Accounting",
    date: "2025-06-01",
  },
  {
    id: 2,
    item: "Router",
    quantity: 5,
    price: 12000,
    department: "IT",
    date: "2025-06-03",
  },
  {
    id: 3,
    item: "Chair",
    quantity: 10,
    price: 8000,
    department: "HR",
    date: "2025-06-10",
  },
  {
    id: 4,
    item: "Desk",
    quantity: 4,
    price: 20000,
    department: "Operations",
    date: "2025-06-12",
  },
];

const PurchaseReport: React.FC = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [dateError, setDateError] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const rowsPerPage = 10;

  async function getPurchaseReport() {
    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("start_date", startDate);
    formData.append("end_date", endDate);

    try {
      setLoading(true);
      const response = await axios.post(
        baseUrl() + "warehouses/purchaseReport",
        formData,
        config
      );
      setFilteredPurchases(response.data?.data);
      setCurrentPage(0); // reset to first page
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  const handleGenerateReport = () => {
    if (!startDate && !endDate) {
      setDateError(true);
      return;
    }
    setDateError(false);
    getPurchaseReport();
  };

  const paginatedRows = filteredPurchases?.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const totalPages = Math.ceil(filteredPurchases?.length / rowsPerPage);

  const changePage = (newPage: number) => {
    setLoading(true);
    setTimeout(() => {
      setCurrentPage(newPage);
      setLoading(false);
    }, 300); // simulate quick load
  };

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar style={{ background: "#1976d2" }}>
          <IonButtons slot="start">
            <IonMenuButton style={{ color: "#1976d2" }} />
          </IonButtons>
          <IonTitle style={{ color: "#1976d2" }}>Purchase Report</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent
        className="ion-padding"
        style={{
          backgroundColor: "#f5f5f5",
          color: "#333",
          fontFamily: "'Roboto', sans-serif",
        }}
      >
        <IonLoading
          isOpen={loading}
          message={"Please wait..."}
          spinner="crescent"
        />

        <Typography
          variant="h5"
          style={{
            color: "#1976d2",
            marginBottom: "24px",
            fontWeight: 500,
          }}
        >
          Generate Report From Audited Purchase Orders
        </Typography>

        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          alignItems="center"
          mb={3}
          style={{
            backgroundColor: "white",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            border: "1px solid #e0e0e0",
          }}
        >
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
              style: { color: "#666" },
            }}
            inputProps={{ style: { color: "#333" } }}
            error={dateError && !startDate}
            helperText={dateError && !startDate ? "Start date required" : ""}
            style={{ backgroundColor: "white", borderRadius: "4px" }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
              style: { color: "#666" },
            }}
            inputProps={{ style: { color: "#333" } }}
            error={dateError && !endDate}
            helperText={dateError && !endDate ? "End date required" : ""}
            style={{ backgroundColor: "white", borderRadius: "4px" }}
          />
          <Button
            variant="contained"
            style={{
              background: "#1976d2",
              color: "white",
              height: "56px",
              padding: "0 24px",
              textTransform: "none",
              fontWeight: 500,
            }}
            onClick={handleGenerateReport}
          >
            Generate Report
          </Button>
        </Box>

        {filteredPurchases?.length > 0 && (
          <Paper
            style={{
              backgroundColor: "white",
              color: "#333",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              border: "1px solid #e0e0e0",
            }}
          >
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: "#1976d2" }}>
                  <TableCell style={{ color: "white", textAlign: "center" }}>
                    Item
                  </TableCell>
                  <TableCell style={{ color: "white", textAlign: "center" }}>
                    Qty
                  </TableCell>
                  <TableCell style={{ color: "white", textAlign: "center" }}>
                    Unit Price (₦)
                  </TableCell>
                  <TableCell style={{ color: "white", textAlign: "center" }}>
                    Total Price (₦)
                  </TableCell>
                  <TableCell style={{ color: "white", textAlign: "center" }}>
                    Date & Time
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows?.map((row, i) => (
                  <TableRow
                    key={i}
                    style={{ backgroundColor: i % 2 ? "#e2e2e2" : "white" }}
                  >
                    <TableCell style={{ textAlign: "center" }}>
                      {row.product_name}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {row.quantity}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      ₦{new Intl.NumberFormat("en-NG").format(row.unit_price)}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      ₦
                      {new Intl.NumberFormat("en-NG").format(
                        row.unit_price * row.quantity
                      )}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {row.request_date}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              p={2}
              gap={2}
            >
              <Button
                variant="outlined"
                onClick={() => changePage(Math.max(currentPage - 1, 0))}
                disabled={currentPage === 0}
              >
                Prev
              </Button>
              <Typography>
                Page {currentPage + 1} of {totalPages}
              </Typography>
              <Button
                variant="outlined"
                onClick={() =>
                  changePage(Math.min(currentPage + 1, totalPages - 1))
                }
                disabled={currentPage >= totalPages - 1}
              >
                Next
              </Button>
            </Box>
          </Paper>
        )}
      </IonContent>
    </IonPage>
  );
};

export default PurchaseReport;
